/**
 * Copyright © IMPACT Commerce AB. All rights reserved.
 * See COPYING.txt for license details.
 */

/**
 * Keeps colourway-thumbnail URLs in sync with the user's selected size.
 *
 * Each thumbnail in `product-variation-item.liquid` carries a
 * `data-size-variants` JSON map of {sizeValue: variantId} for its target
 * product. On size change we look up the current size in every map and
 * rewrite the thumbnail's URL to `…?variant=<id>` — so navigating to a
 * sibling colour lands on the matching size, server-rendered, no flicker.
 */

const SIZE_FIELDSET_SELECTOR = 'fieldset.variant-option--grid[data-current-region]';
const NAVIGATE_THUMB_SELECTOR =
  'product-variations[data-selection-mode="navigate"] .product-card__thumbnail[data-size-variants]';

/** @returns {string | null} */
function readCurrentSize() {
  const fieldset = document.querySelector(SIZE_FIELDSET_SELECTOR);
  if (!fieldset) return null;
  const checked = fieldset.querySelector('input[type="radio"]:checked');
  if (!(checked instanceof HTMLInputElement)) return null;
  return checked.value || null;
}

/**
 * Rewrites every colourway thumbnail's URL for the current size. When a
 * target doesn't carry the size, the URL resets to bare so a stale variant
 * param can't leak between size selections.
 */
function syncColourwayUrls() {
  const currentSize = readCurrentSize();

  for (const thumb of document.querySelectorAll(NAVIGATE_THUMB_SELECTOR)) {
    if (!(thumb instanceof HTMLElement)) continue;

    // Card-context thumbnails are driven by card-variation-hover.js instead.
    if (thumb.closest('product-card') || thumb.closest('.resource-card')) continue;

    /** @type {Record<string, number | string> | null} */
    let map = null;
    try {
      map = JSON.parse(thumb.dataset.sizeVariants || 'null');
    } catch {
      continue;
    }
    if (!map || typeof map !== 'object') continue;

    const currentUrl = thumb.dataset.variationUrl || thumb.getAttribute('href') || '';
    const baseUrl = currentUrl.split('?')[0];
    if (!baseUrl) continue;

    const variantId = currentSize ? map[currentSize] : undefined;
    const nextUrl = variantId ? `${baseUrl}?variant=${variantId}` : baseUrl;

    thumb.dataset.variationUrl = nextUrl;
    if (thumb instanceof HTMLAnchorElement) {
      thumb.href = nextUrl;
    }
  }
}

document.addEventListener('change', (event) => {
  const target = event.target;
  if (!(target instanceof HTMLInputElement)) return;
  if (target.type !== 'radio') return;
  if (!target.closest(SIZE_FIELDSET_SELECTOR)) return;
  syncColourwayUrls();
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', syncColourwayUrls, { once: true });
} else {
  syncColourwayUrls();
}

// Re-sync after each soft-nav morph — the new section's thumbnails carry
// the next product's size-variants maps.
document.addEventListener('shopify:section:load', syncColourwayUrls);
